import React, { useState, useEffect, useMemo } from "react";
import axios from '../api/axiosConfig';
import {
    Box,
    Pagination,
    Skeleton,
    Chip,
    Typography,
    Stack,
    Avatar,
    Paper,
    Divider,
    TextField,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    Button,
} from "@mui/material";
// import Layout from "../components/Layout";
import * as XLSX from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { InputBase } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { useTheme, useMediaQuery } from "@mui/material";
import RecordDetailModal from "../components/RecordDetailModal";
import { getInitials, stringToColor } from "../utils/DataHelper";


export default function MemberBetania() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("all");
    const [genderFilter, setGenderFilter] = useState("all");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [toast, setToast] = useState({
        open: false,
        severity: "success",
        message: "",
    });
    const rowsPerPage = 100;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isDark = theme.palette.mode === "dark";

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const handleOpenDetail = (row) => {
        setSelectedRecord(row);
        setModalOpen(true);
    };



    // ------------------------
    // 🔥 API CALL HERE
    // ------------------------
    const fetchData = async (query = {}) => {
        try {
            setLoading(true);

            const params = new URLSearchParams(query).toString();
            const url = `/jemaat.php?isbetaniamember=1${params}`;

            const resp = await axios.get(url);
            setRows(resp.data); // API must return array of rows
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };


    // Load initial data
    useEffect(() => {
        fetchData();
    }, []);

    // ------------------------
    // 🔍 Client-side filtering
    // ------------------------
    const filteredRows = useMemo(() => {
        const term = search.toLowerCase();
        return rows.filter((r) => {
            const matchSearch =
                !term ||
                r.name_full?.toLowerCase().includes(term) ||
                r.idjemaat?.toLowerCase().includes(term) ||
                r.phone_mobile?.toLowerCase().includes(term);

            const matchStatus =
                statusFilter === "all" ||
                (r.status_jemaat || "").toLowerCase() === statusFilter;

            const matchGender =
                genderFilter === "all" ||
                (r.gender || "").toLowerCase() === genderFilter;

            const dateValue = r.betaniamemberestablished || r.dtcreate;
            const date = dateValue ? dayjs(dateValue) : null;
            const matchStart = startDate ? date && date.isAfter(startDate.startOf("day").subtract(1, "ms")) : true;
            const matchEnd = endDate ? date && date.isBefore(endDate.endOf("day").add(1, "ms")) : true;

            return matchSearch && matchStatus && matchGender && matchStart && matchEnd;
        });
    }, [rows, search, statusFilter, genderFilter, startDate, endDate]);

    const exportToExcel = () => {
        const exportData = filteredRows.map(row => ({
            ID: row.idjemaat,
            FullName: row.name_full,
            Nick: row.name_nick,
            Phone: row.phone_mobile,
            Status: row.status_jemaat,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "MemberBetania");

        XLSX.writeFile(wb, "memberbetania.xlsx");
    };

    // ------------------------
    // 📄 Pagination (100 rows)
    // ------------------------

    const paginatedRows = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredRows.slice(start, start + rowsPerPage);
    }, [filteredRows, page]);

    const totalMembers = rows.length;
    const activeMembers = rows.filter(
        (r) => (r.status_jemaat || "").toLowerCase() === "aktif" || (r.status_jemaat || "").toLowerCase() === "active"
    ).length;
    const inactiveMembers = rows.filter(
        (r) => (r.status_jemaat || "").toLowerCase() === "nonaktif" || (r.status_jemaat || "").toLowerCase() === "inactive"
    ).length;
    const filteredCount = filteredRows.length;

    return (
        < >

            <Box sx={{
                height: isMobile ? "calc(100dvh - 80px)" : "calc(100dvh - 26px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                background: isDark
                    ? "linear-gradient(180deg, rgba(2,6,23,0.95) 0%, rgba(15,23,42,0.95) 100%)"
                    : "linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(248,250,252,1) 100%)",
            }}>
                <Box sx={{ px: isMobile ? 1.5 : 2.5, pt: isMobile ? 1.5 : 2 }}>
                    <Typography variant="h5"
                        sx={{
                            mb: 0.5,
                            fontWeight: 800,
                            color: isDark ? "#f8fafc" : "#0f172a",
                            letterSpacing: "0.3px",
                        }}>
                        Anggota Betania
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Daftar member Betania dengan informasi ringkas dan akses cepat.
                    </Typography>
                </Box>

                <Box sx={{ px: isMobile ? 1.5 : 2.5, mt: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                background: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                                flex: 1,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Total Member
                            </Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {totalMembers.toLocaleString()}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                background: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                                flex: 1,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Active
                            </Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {activeMembers.toLocaleString()}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                background: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                                flex: 1,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Inactive
                            </Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {inactiveMembers.toLocaleString()}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider",
                                background: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                                flex: 1,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                Filtered
                            </Typography>
                            <Typography variant="h6" fontWeight={800}>
                                {filteredCount.toLocaleString()}
                            </Typography>
                        </Paper>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0,
                        px: isMobile ? 1.5 : 2.5,
                        alignItems: "center",
                        mt: 2,
                    }}
                >
                    {/* Google Search Box */}
                    <Paper
                        elevation={0}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            maxWidth: 500,
                            p: "8px 14px",
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            background: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                            boxShadow: isDark ? "0 6px 16px rgba(0,0,0,0.35)" : "0 6px 16px rgba(15,23,42,0.08)",
                            transition: "0.2s ease",
                            "&:hover": {
                                boxShadow: isDark ? "0 10px 20px rgba(0,0,0,0.45)" : "0 10px 20px rgba(15,23,42,0.12)",
                            },
                            "&:focus-within": {
                                borderColor: "#2563eb",
                                boxShadow: "0 0 0 3px rgba(37,99,235,0.15)",
                            },
                        }}
                    >
                        <SearchIcon sx={{ opacity: 0.6, mr: 1 }} />

                        <InputBase
                            placeholder="Cari Anggota Betania..."
                            fullWidth
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            sx={{
                                fontSize: "1rem",
                                ml: 1,
                            }}
                        />

                        {search.length > 0 && (
                            <IconButton
                                onClick={() => setSearch("")}
                                size="small"
                                sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </Paper>


                    {/* Export Button */}
                    
                     <IconButton
                        variant="contained"
                        color="success"
                        onClick={exportToExcel}
                        sx={{
                            ml: 2,
                            height: 44,
                            width: 44,
                            borderRadius: 2,
                            textTransform: "none",
                            px: 0,
                            backgroundColor: "success.main",
                            color: "white",
                            boxShadow: "0px 8px 18px rgba(22,163,74,0.28)",
                            "&:hover": {
                                backgroundColor: "success.dark",
                            },
                        }}
                    >
                        <DownloadIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: isMobile ? 1.5 : 2.5, mt: 2 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            background: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
                        }}
                    >
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                            <TextField
                                select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                size="small"
                                SelectProps={{ native: true }}
                                sx={{ minWidth: 140 }}
                            >
                                <option value="all">All</option>
                                <option value="aktif">Aktif</option>
                                <option value="nonaktif">Nonaktif</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </TextField>
                            <TextField
                                select
                                label="Gender"
                                value={genderFilter}
                                onChange={(e) => setGenderFilter(e.target.value)}
                                size="small"
                                SelectProps={{ native: true }}
                                sx={{ minWidth: 140 }}
                            >
                                <option value="all">All</option>
                                <option value="l">Laki-laki</option>
                                <option value="p">Perempuan</option>
                            </TextField>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    slotProps={{ textField: { size: "small" } }}
                                />
                                <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    slotProps={{ textField: { size: "small" } }}
                                />
                            </LocalizationProvider>
                            <Box flex={1} />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setStatusFilter("all");
                                    setGenderFilter("all");
                                    setStartDate(null);
                                    setEndDate(null);
                                }}
                            >
                                Reset Filter
                            </Button>
                        </Stack>
                    </Paper>
                </Box>


                {/* Main container */}
                {/* <Card sx={{ borderRadius: 0, overflow: "hidden", boxShadow: 1, mt: 2 }}>
                    <CardContent sx={{ p: 0,sx:{overflowX:'auto',background:'#DDDDDD'}    }}> */}

                <Box sx={{
                    flex: 1,               // 🔥 fills available height
                    overflowY: "auto",
                    mt: 1,
                    borderRadius: 0,
                    px: isMobile ? 1.5 : 2.5,
                    WebkitOverflowScrolling: "touch",
                }}>
                    {/* Skeleton Loader */}
                    {loading ? (
                        [...Array(12)].map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                height={56}
                                sx={{ my: 0.6, borderRadius: 2 }}
                            />
                        ))
                    ) : (

                        <>


                            <Stack spacing={1.5}>
                                {paginatedRows.map((row) => {
                                    const avatarColor = stringToColor(row.name_full);

                                    return (
                                        <Box
                                            key={row.id}
                                            onClick={() => handleOpenDetail(row)}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                bgcolor: "background.paper",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
                                                cursor: "pointer",
                                                transition: "all .2s",
                                                "&:hover": {
                                                    bgcolor: isDark ? "rgba(148,163,184,0.12)" : "rgba(248,250,252,0.9)",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 12px 24px rgba(15,23,42,0.12)",
                                                },
                                            }}
                                        >
                                            {/* Header */}
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                {/* Avatar */}
                                                <Avatar
                                                    sx={{
                                                        bgcolor: avatarColor,
                                                        color: "#fff",
                                                        fontWeight: 700,
                                                        width: 42,
                                                        height: 42,
                                                        fontSize: 15,
                                                    }}
                                                >
                                                    {getInitials(row.name_full)}
                                                </Avatar>

                                                <Box flex={1}>
                                                    <Typography fontSize={18} fontWeight={700}>
                                                        {row.name_full}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {row.alamat}
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    label={row.idjemaat}
                                                    color={"primary"}
                                                    size="small"
                                                    sx={{ fontWeight: 700 }}
                                                />
                                                <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                                                    <Tooltip title="Copy ID">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigator.clipboard?.writeText(row.idjemaat || "");
                                                                setToast({
                                                                    open: true,
                                                                    severity: "success",
                                                                    message: "ID copied.",
                                                                });
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View Profile">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenDetail(row);
                                                            }}
                                                        >
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>


                                        </Box>
                                    );
                                })}
                            </Stack>



                        </>
                    )}

                </Box>

                {!loading && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            borderTop: theme => `1px solid ${theme.palette.divider}`,
                            bgcolor: "background.paper",
                            px: isMobile ? 1.5 : 2.5,
                            py: 1,
                            pb: "env(safe-area-inset-bottom)", // iOS fix
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={isMobile ? 0.5 : 2}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Pagination
                                count={Math.ceil(filteredRows.length / rowsPerPage)}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                                siblingCount={0}
                                boundaryCount={1}
                                shape="rounded"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    boxShadow: "none",
                                    "& .MuiPaginationItem-root": {
                                        borderRadius: "10px",
                                        fontSize: "13px",
                                        minWidth: 34,
                                        height: 34,
                                        bgcolor: isDark ? "rgba(148,163,184,0.2)" : "#f1f5f9",
                                        color: "text.primary",
                                    },

                                    "& .MuiPaginationItem-root:hover": {
                                        bgcolor: "action.hover",
                                    },

                                    "& .Mui-selected": {
                                        bgcolor: isDark ? "#f8fafc" : "#0f172a",
                                        color: isDark ? "#0f172a" : "#fff",
                                        "&:hover": {
                                            bgcolor: isDark ? "#e2e8f0" : "#111827",
                                        },
                                    },
                                }}
                            />

                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: isDark ? "rgba(148,163,184,0.15)" : "#f8fafc",
                                    borderRadius: 2,
                                    textAlign: "center",
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 18,
                                        fontWeight: 600,
                                        color: "text.primary",
                                    }}
                                >
                                    {filteredRows.length.toLocaleString()}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 500,
                                        color: "text.secondary",
                                    }}
                                >
                                    Record
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                )}

                <Snackbar
                    open={toast.open}
                    autoHideDuration={2500}
                    onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>

                {/* </CardContent>

                </Card> */}
            </Box>
            <RecordDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                data={selectedRecord}
            />
        </ >
    );
}





//
//  STYLES
//
// const thStyle = {
//     padding: "12px",
//     textAlign: "left",
//     fontWeight: 600,
//     color: "#444",
//     borderBottom: "1px solid #e0e0e0",
//     fontSize: "13px",
// };

// const tdStyle = {
//     padding: "12px",
//     borderBottom: "1px solid #f2f2f2",
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     fontSize: "13px",
// };

// const rowStyle = {
//     background: "#fff",
//     transition: "background 0.15s",
//     cursor: "pointer",
// };
